import React, {
  useState,
  useEffect,
} from 'react';
import {
  useNavigate,
} from 'react-router-dom';
import {
  useAuth,
} from '../contexts/AuthContext';
import {
  useRole,
} from '../hooks/useRole';
import Chat from './chat/Chat';
import RecentAchievements from './achievements/RecentAchievements';
import barService from '../services/bar';
import gulagService from '../services/gulag';
import achievementsService from '../services/achievements';
import {
  eventsService,
} from '../services/events';
import EventHero from './event/EventHero';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';
import {
  SportsEsports as GulagIcon,
  LocalBar as BarIcon,
  EmojiEvents as TrophyIcon,
  Whatshot as FireIcon,
  ArrowForward as ArrowIcon,
  RestaurantMenu as MenuIcon,
} from '@mui/icons-material';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { roleDisplayName, roleColor } = useRole();
  
  // State
  const [barTransactions, setBarTransactions] = useState([]);
  const [barPurchaseCount, setBarPurchaseCount] = useState(0);
  const [barTotal, setBarTotal] = useState(0);
  const [eventRanking, setEventRanking] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);
  const [achievementCount, setAchievementCount] = useState(0);
  const [activeEvent, setActiveEvent] = useState(null);
  const [activeEventDetails, setActiveEventDetails] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user) return;
      
      setDataLoading(true);
      try {
        const userId = user._id || user.id;
        
        const activeEventResult = await eventsService.getActiveEvent().catch(() => ({ success: false, data: null }));
        const currentEvent = activeEventResult.success ? activeEventResult.data : null;
        setActiveEvent(currentEvent);

        const [barResult, gulagStatsResult, achievementsResult, fullEventResult] = await Promise.all([
          currentEvent?.id
            ? barService.getMyTransactions(currentEvent.id).catch(() => ({ success: false, data: [] }))
            : Promise.resolve({ success: true, data: [] }),
          gulagService.getMyStats().catch(() => ({ success: false, data: null })),
          achievementsService.getUserAchievements(userId, true).catch(() => ({ success: false, data: [] })),
          currentEvent?.id
            ? eventsService.getEvent(currentEvent.id).catch(() => ({ success: false, data: null }))
            : Promise.resolve({ success: true, data: null })
        ]);

        setActiveEventDetails(fullEventResult.success ? fullEventResult.data : null);

        // Process bar transactions for the active event only
        if (barResult.success && barResult.data) {
          setBarTransactions(barResult.data.slice(0, 3));
          setBarPurchaseCount(barResult.data.length);
          const total = barResult.data.reduce((sum, t) => sum + ((t.price || 0) * (t.quantity || 1)), 0);
          setBarTotal(total);
        } else {
          setBarTransactions([]);
          setBarPurchaseCount(0);
          setBarTotal(0);
        }

        // Process gulag stats for the active event only
        if (gulagStatsResult.success && gulagStatsResult.data && currentEvent?.id) {
          const rankings = gulagStatsResult.data.event_rankings || [];
          const ranking = rankings.find((entry) => entry.event_id === currentEvent.id) || null;
          setEventRanking(ranking);
          const matches = (gulagStatsResult.data.recent_matches || [])
            .filter((match) => match.event_id === currentEvent.id);
          setRecentMatches(matches.slice(0, 3));
        } else {
          setEventRanking(null);
          setRecentMatches([]);
        }

        // Process achievements
        if (achievementsResult.success && achievementsResult.data) {
          setAchievementCount(achievementsResult.data.length);
        }
      } catch (error) {
        console.error('Dashboard data loading error:', error);
      } finally {
        setDataLoading(false);
      }
    };

    if (!loading && user) {
      loadDashboardData();
    }
  }, [loading, user]);

  // Hero Stat Card Component
  const HeroStatCard = ({ title, value, icon, color = 'primary', subtitle }) => (
    <Card 
      sx={{ 
        height: '100%',
        backgroundColor: '#241E18',
        border: `1px solid ${alpha(color === 'primary' ? '#C9A227' : color === 'secondary' ? '#3D7A8C' : color === 'success' ? '#6B8F71' : '#3D7A8C', 0.2)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: `0 8px 24px ${alpha(color === 'primary' ? '#C9A227' : color === 'secondary' ? '#3D7A8C' : color === 'success' ? '#6B8F71' : '#3D7A8C', 0.3)}`
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 0.5 }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar 
            sx={{ 
              bgcolor: color === 'primary' ? 'primary.main' : color === 'secondary' ? 'secondary.main' : color === 'success' ? 'success.main' : 'info.main',
              width: 56, 
              height: 56 
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  // Bar Spending Card Component
  const BarSpendingCard = () => (
    <Paper 
      sx={{ 
        p: 3, 
        mb: 3,
        backgroundColor: '#241E18',
        border: '1px solid rgba(201, 162, 39, 0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BarIcon sx={{ color: 'secondary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Bar & Catering
          </Typography>
        </Box>
        <Chip 
          label={`CHF ${barTotal.toFixed(2)}`} 
          color="secondary"
          sx={{ fontWeight: 700, fontSize: '1rem' }}
        />
      </Box>
      
      {barTransactions.length > 0 ? (
        <>
          <Box sx={{ mb: 2 }}>
            {barTransactions.map((transaction, index) => (
              <Box 
                key={transaction._id || index}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  py: 1.5,
                  borderBottom: index < barTransactions.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <MenuIcon sx={{ color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {transaction.drink_name || 'Item'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {transaction.quantity}x • {new Date(transaction.timestamp).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                  CHF {((transaction.price || 0) * (transaction.quantity || 1)).toFixed(2)}
                </Typography>
              </Box>
            ))}
          </Box>
          <Button 
            fullWidth 
            variant="outlined" 
            color="secondary"
            endIcon={<ArrowIcon />}
            onClick={() => navigate('/bar')}
          >
            View Full Tab
          </Button>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          No purchases yet for this event. Visit the bar to get started!
        </Typography>
      )}
    </Paper>
  );

  // Gulag Activity Card Component
  const GulagActivityCard = () => (
    <Paper 
      sx={{ 
        p: 3, 
        mb: 3,
        backgroundColor: '#241E18',
        border: '1px solid rgba(201, 162, 39, 0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GulagIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Gulag Activity
          </Typography>
        </Box>
        {eventRanking && (
          <Chip 
            icon={<FireIcon />}
            label={`#${eventRanking.rank_position || 'Unranked'}`} 
            color="primary"
            sx={{ fontWeight: 700 }}
          />
        )}
      </Box>

      {eventRanking && (
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`${eventRanking.wins || 0}W - ${eventRanking.losses || 0}L`}
            variant="outlined"
            color="primary"
          />
          <Chip 
            label={`${eventRanking.total_points?.toFixed(0) || 0} pts`}
            variant="outlined"
            color="secondary"
          />
          <Chip 
            label={`${eventRanking.win_rate?.toFixed(1) || 0}% WR`}
            variant="outlined"
            color={eventRanking.win_rate >= 50 ? 'success' : 'default'}
          />
        </Box>
      )}

      {recentMatches.length > 0 ? (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Recent Matches
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Game</TableCell>
                  <TableCell>Opponent</TableCell>
                  <TableCell align="center">Result</TableCell>
                  <TableCell align="right">Points</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentMatches.map((match, index) => {
                  // Determine if user is participant1 or participant2
                  const userId = user?._id || user?.id;
                  const isParticipant1 = match.participant1?.id === userId;
                  const opponent = isParticipant1 ? match.participant2 : match.participant1;
                  
                  // Determine win/loss
                  const isWinner = match.winner?.id === userId;
                  const result = match.winner ? (isWinner ? 'win' : 'loss') : 'draw';
                  
                  return (
                    <TableRow key={match._id || match.id || index}>
                      <TableCell>
                        <Typography variant="caption">
                          {match.game_name || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {opponent?.name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={result.toUpperCase()}
                          size="small"
                          color={result === 'win' ? 'success' : result === 'loss' ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {match.points_awarded?.toFixed(0) || 0}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2, mb: 2 }}>
          {activeEvent ? 'No matches played yet for this event. Challenge someone!' : 'No active event is set.'}
        </Typography>
      )}

      <Button 
        fullWidth 
        variant="contained" 
        color="primary"
        startIcon={<GulagIcon />}
        onClick={() => navigate('/gulag')}
      >
        Go to Gulag
      </Button>
    </Paper>
  );

  if (loading || dataLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  const wins = eventRanking?.wins || 0;
  const losses = eventRanking?.losses || 0;
  const currentRank = eventRanking?.rank_position || '-';
  const currentUserId = user?.id || user?._id;
  const participants = activeEventDetails?.participants || [];
  const isRegistered = Boolean(currentUserId && participants.includes(currentUserId));
  const showSignupHero = Boolean(activeEventDetails && !isRegistered);

  if (showSignupHero) {
    return <EventHero event={activeEventDetails} />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
            Welcome back, {user?.username || user?.email?.split('@')[0] || 'Player'}!
          </Typography>
          <Chip 
            label={roleDisplayName}
            sx={{ 
              backgroundColor: roleColor,
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        </Box>
        <Typography variant="h6" color="text.secondary">
          Your event command center{isRegistered && activeEvent?.name ? ` • ${activeEvent.name}` : ''}
        </Typography>
      </Box>

      {/* Hero Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <HeroStatCard 
            title="Bar Tab" 
            value={`CHF ${barTotal.toFixed(2)}`}
            icon={<BarIcon />} 
            color="secondary"
            subtitle={`${barPurchaseCount} purchases`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <HeroStatCard 
            title="Gulag Record" 
            value={`${wins}W-${losses}L`}
            icon={<GulagIcon />} 
            color="primary"
            subtitle={eventRanking ? `${eventRanking.win_rate?.toFixed(1)}% win rate` : 'No matches yet'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <HeroStatCard 
            title="Achievements" 
            value={achievementCount}
            icon={<TrophyIcon />} 
            color="success"
            subtitle="Unlocked"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <HeroStatCard 
            title="Current Rank" 
            value={currentRank === '-' ? 'Unranked' : `#${currentRank}`}
            icon={<TrophyIcon />} 
            color="info"
            subtitle={eventRanking ? `${eventRanking.total_points?.toFixed(0) || 0} points` : 'Play to get ranked'}
          />
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} lg={8}>
          <BarSpendingCard />
          
          <GulagActivityCard />
          
          <Box sx={{ mb: 3 }}>
            <RecentAchievements userId={user?._id || user?.id} limit={5} />
          </Box>
        </Grid>

        {/* Right Column - Chat */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ position: 'sticky', top: 16 }}>
            <Chat />
            
            {/* Partner Logos */}
            <Paper 
              sx={{ 
                mt: 3,
                p: 3,
                backgroundColor: '#241E18',
                border: '1px solid rgba(201, 162, 39, 0.1)',
                textAlign: 'center'
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                Our Partners
              </Typography>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: 3,
                  flexWrap: 'wrap'
                }}
              >
                <Box
                  component="img"
                  src="/oclogo.svg"
                  alt="Old Capitol"
                  sx={{
                    height: 40,
                    objectFit: 'contain',
                    opacity: 0.8,
                    transition: 'opacity 0.3s ease',
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                />
                <Box
                  component="img"
                  src="/quickline.png"
                  alt="Quickline"
                  sx={{
                    height: 40,
                    objectFit: 'contain',
                    opacity: 0.8,
                    transition: 'opacity 0.3s ease',
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                />
                <Box
                  component="img"
                  src="/renet.png"
                  alt="Renet"
                  sx={{
                    height: 40,
                    objectFit: 'contain',
                    opacity: 0.8,
                    transition: 'opacity 0.3s ease',
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                />
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
